import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import type { GuideNavId, GuideNavItem } from '../content/walletNav';
import { GUIDE_ACCENT, guideTokens } from '../theme/guideTokens';

interface GuideSidebarProps {
  items: GuideNavItem[];
  activeId: GuideNavId;
  onSelect: (id: GuideNavId) => void;
  variant?: 'rail' | 'chips';
}

export const GuideSidebar: React.FC<GuideSidebarProps> = ({
  items,
  activeId,
  onSelect,
  variant = 'rail',
}) => {
  if (variant === 'chips') {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 1,
          mb: 2,
          mx: -0.5,
          px: 0.5,
          scrollbarWidth: 'thin',
        }}
      >
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <Chip
              key={item.id}
              label={item.comingSoon ? `${item.label} · Soon` : item.label}
              onClick={() => onSelect(item.id)}
              variant={active ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0,
                fontWeight: 700,
                fontFamily: guideTokens.fontDisplay,
                opacity: item.comingSoon && !active ? 0.7 : 1,
                ...(active
                  ? {
                      bgcolor: GUIDE_ACCENT,
                      color: '#fff',
                      borderColor: GUIDE_ACCENT,
                      '&:hover': { bgcolor: guideTokens.accentHover },
                    }
                  : {
                      borderColor: 'divider',
                      color: 'text.primary',
                    }),
              }}
            />
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      aria-label="Wallet guide sections"
      sx={{
        position: { md: 'sticky' },
        top: { md: 88 },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          letterSpacing: '0.08em',
          mb: 0.5,
          px: 1.25,
          fontFamily: guideTokens.fontDisplay,
          fontWeight: 700,
        }}
      >
        Guide
      </Typography>
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <Box
            key={item.id}
            component="button"
            type="button"
            onClick={() => onSelect(item.id)}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              fontFamily: guideTokens.fontDisplay,
              fontSize: '0.875rem',
              fontWeight: active ? 700 : 500,
              color: active ? GUIDE_ACCENT : 'text.primary',
              bgcolor: active
                ? (t) => guideTokens.accentSoft(t.palette.mode === 'dark')
                : 'transparent',
              border: '1px solid',
              borderColor: active
                ? (t) => guideTokens.accentBorder(t.palette.mode === 'dark')
                : 'transparent',
              borderLeft: active ? `3px solid ${GUIDE_ACCENT}` : '3px solid transparent',
              transition: 'background .15s ease, border-color .15s ease',
              '&:hover': {
                bgcolor: (t) =>
                  active
                    ? guideTokens.accentSoft(t.palette.mode === 'dark')
                    : t.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.04)'
                      : 'rgba(0,0,0,0.03)',
              },
            }}
          >
            <span>{item.label}</span>
            {item.comingSoon && (
              <Chip
                label="Soon"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  fontFamily: guideTokens.fontDisplay,
                  bgcolor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
